import { useCallback, useMemo } from "react";
import {
  UseFormSetValue,
  UseFieldArrayRemove,
  UseFormWatch,
} from "react-hook-form";
import { PayeeFormInputs } from "./types";

interface UsePaymentDocumentDeletionProps {
  paymentId?: string;
  existingPayment?: any;
  formFields: any[];
  remove: UseFieldArrayRemove;
  setValue: UseFormSetValue<PayeeFormInputs>;
  watch: UseFormWatch<PayeeFormInputs>;
  deleteDocument: any;
  hasUnsavedChanges: boolean;
}

/**
 * Custom hook that provides unified document deletion logic for both new and existing payments.
 *
 * Deletion Rules:
 * - New payments: All documents are pending → Always deletable
 * - Existing payments: Mixed saved/pending → Delete based on document status and context
 * - Pending documents: Always deletable (not yet saved to payment)
 * - Saved documents: Only deletable if payment allows editing
 */
export const usePaymentDocumentDeletion = ({
  paymentId,
  existingPayment,
  formFields,
  remove,
  setValue,
  watch,
  deleteDocument,
  hasUnsavedChanges,
}: UsePaymentDocumentDeletionProps) => {
  const handleDocumentDeletion = useCallback(
    (documentId: string, sourceType: "invoice" | "receipt") => {
      // For new payments: Delete from form state and database
      if (!paymentId) {
        if (sourceType === "invoice") {
          const invoiceIndex = formFields.findIndex(
            (invoice) => invoice.invoiceDocumentId === documentId
          );
          if (invoiceIndex !== -1) {
            deleteDocument({ variables: { id: documentId } });
            remove(invoiceIndex);
          }
        } else if (sourceType === "receipt") {
          deleteDocument({ variables: { id: documentId } });
          setValue("receiptDocument", undefined);
        }
        return;
      }

      // For existing payments: Handle based on document status and context
      if (paymentId && existingPayment) {
        if (sourceType === "receipt" && hasUnsavedChanges) {
          const originalReceipt = existingPayment.document;
          const currentReceiptDocument = watch("receiptDocument");

          // Check if this is a pending receipt (different from original)
          if (
            currentReceiptDocument &&
            currentReceiptDocument.id !== originalReceipt?.id
          ) {
            // Delete the newly uploaded receipt
            deleteDocument({ variables: { id: documentId } });
            // Revert to original receipt
            setValue("receiptDocument", originalReceipt || undefined);
          }
        }
        // Note: Invoice deletion for existing payments would be handled here
        // Currently not implemented in the original component
      }
    },
    [
      paymentId,
      existingPayment,
      formFields,
      remove,
      setValue,
      watch,
      deleteDocument,
      hasUnsavedChanges,
    ]
  );

  // Determine if deletion is allowed based on payment state
  const allowDeletion = useMemo(() => {
    if (!paymentId) return true; // New payments always allow deletion
    return hasUnsavedChanges; // Existing payments only when there are unsaved changes
  }, [paymentId, hasUnsavedChanges]);

  return {
    handleDocumentDeletion,
    allowDeletion,
  };
};
